
import { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { useLanguage } from '@/context/LanguageContext';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { therapistsData } from '@/data/therapists';
import { 
  MapPin, 
  Star, 
  Video, 
  Clock,
  Mail,
  Phone,
  ArrowLeft,
  User,
  Award,
  BookOpen,
  Heart,
  Calendar
} from 'lucide-react';
import TherapistBookingCard from '@/components/therapists/TherapistBookingCard';
import { useToast } from '@/components/ui/use-toast';
import { TimeSlot, SpecificDateAvailability } from '@/components/therapists/CalendarIntegration';
import { MobileStickyCTA } from '@/components/conversion/MobileStickyCTA';

const mockWeeklyAvailability: TimeSlot[] = [
  { id: '1', day: 1, start: '09:00', end: '12:00' },
  { id: '2', day: 1, start: '13:00', end: '17:00' },
  { id: '3', day: 2, start: '09:00', end: '12:00' },
  { id: '4', day: 2, start: '13:00', end: '17:00' },
  { id: '5', day: 3, start: '14:00', end: '19:00' },
  { id: '6', day: 4, start: '09:00', end: '12:00' },
  { id: '7', day: 4, start: '13:00', end: '17:00' },
  { id: '8', day: 5, start: '09:00', end: '13:00' },
];

const mockSpecificDates: SpecificDateAvailability[] = [
  {
    id: '1',
    date: new Date(new Date().getFullYear(), new Date().getMonth(), new Date().getDate() + 5),
    slots: [
      { start: '10:00', end: '12:00' },
      { start: '14:00', end: '16:00' },
    ]
  }
];

const mockExistingAppointments = [
  new Date(new Date().getFullYear(), new Date().getMonth(), new Date().getDate() + 2, 10, 0),
  new Date(new Date().getFullYear(), new Date().getMonth(), new Date().getDate() + 2, 14, 0),
  new Date(new Date().getFullYear(), new Date().getMonth(), new Date().getDate() + 3, 11, 0),
];

// Expanded therapist bio information for profiles
const therapistBios = {
  "th-001": {
    summary: "I specialize in evidence-based approaches for anxiety and depression, with over 10 years of experience helping clients develop practical coping strategies and achieve lasting positive change.",
    fullBio: "As a certified psychotherapist with extensive training in cognitive-behavioral therapy and trauma-informed care, I'm dedicated to providing a safe, non-judgmental space where clients can explore their concerns and work toward healing. My approach is collaborative and compassionate, focusing on your unique strengths while addressing challenges that may be holding you back.\n\nI believe that therapy should be accessible and practical, giving you tools you can use in your everyday life. Whether you're dealing with long-standing issues or navigating a recent crisis, I'm here to support your journey toward greater wellbeing and resilience.",
    approach: "My therapeutic approach combines evidence-based techniques from Cognitive Behavioral Therapy (CBT), Acceptance and Commitment Therapy (ACT), and mindfulness practices. I tailor these approaches to meet each client's unique needs and goals.",
    education: [
      "Ph.D. in Clinical Psychology, Stanford University",
      "M.A. in Counseling Psychology, Columbia University",
      "Certified in Trauma-Focused Cognitive Behavioral Therapy"
    ]
  },
  "th-002": {
    summary: "I help individuals navigate relationship challenges, grief, and work-related stress using a client-centered approach that emphasizes personal growth and improved wellbeing.",
    fullBio: "With a background in both clinical psychology and organizational consulting, I bring a unique perspective to my therapy practice. I specialize in helping clients who are dealing with relationship issues, workplace stress, and life transitions. My approach is warm, direct, and focused on practical solutions while honoring the emotional complexities of each situation.\n\nI believe that everyone deserves a fulfilling life with meaningful connections and purpose. Through our work together, we'll identify patterns that may be holding you back and develop strategies to create the changes you want to see in your relationships and career.",
    approach: "I practice an integrative approach that draws from Psychodynamic Therapy, Solution-Focused Brief Therapy, and Emotionally Focused Therapy. I believe in addressing both past influences and present challenges to create meaningful change.",
    education: [
      "Psy.D. in Clinical Psychology, University of California, Berkeley",
      "B.A. in Psychology with honors, Harvard University",
      "Advanced training in Emotionally Focused Therapy"
    ]
  }
};

const TherapistProfile = () => {
  const { id } = useParams<{ id: string }>();
  const { t } = useLanguage();
  const [weeklyAvailability, setWeeklyAvailability] = useState<TimeSlot[]>(mockWeeklyAvailability);
  const [specificDates, setSpecificDates] = useState<SpecificDateAvailability[]>(mockSpecificDates);
  const [existingAppointments, setExistingAppointments] = useState<Date[]>(mockExistingAppointments);
  const [userBalance, setUserBalance] = useState(0);
  const [activeTab, setActiveTab] = useState("about");
  
  const therapist = therapistsData.find(t => t.id === id);
  // Get bio information if available, or use default
  const bioInfo = id ? therapistBios[id as keyof typeof therapistBios] : undefined;
  
  useEffect(() => {
    const isLoggedIn = localStorage.getItem('clientLoggedIn') === 'true';
    
    if (isLoggedIn) {
      setUserBalance(150);
    }
  }, []);
  
  const handleTabChange = (value: string) => {
    setActiveTab(value);
  };
  
  const scrollToBookingSection = () => {
    const bookingElement = document.getElementById('booking-section');
    if (bookingElement) {
      bookingElement.scrollIntoView({ behavior: 'smooth' });
    }
  };
  
  if (!therapist) {
    return (
      <div className="container mx-auto px-4 py-16 text-center">
        <h1 className="text-2xl font-bold text-brand-gray-900 mb-4">
          {t('therapistNotFound')}
        </h1>
        <Button asChild>
          <Link to="/find">
            <ArrowLeft className="mr-2 h-4 w-4" />
            {t('backToSearch')}
          </Link>
        </Button>
      </div>
    );
  }
  
  return (
    <>
      <MobileStickyCTA 
        variant="booking" 
        therapistId={id}
        onAction={scrollToBookingSection}
      />
      <div className="container mx-auto px-4 py-6 md:py-8">
        <div className="mb-4 md:mb-6 flex items-center">
          <Button variant="ghost" size="sm" asChild className="mr-4">
            <Link to="/find">
              <ArrowLeft className="mr-1 h-4 w-4" />
              {t('backToSearch')}
            </Link>
          </Button>
        </div>
        
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          {/* Left column - Therapist info */}
          <div className="md:col-span-2">
            <div className="bg-white rounded-xl shadow-md overflow-hidden mb-6">
              <div className="p-4 sm:p-6 border-b border-brand-gray-100">
                <div className="flex flex-col sm:flex-row">
                  <div className="mb-4 sm:mb-0 sm:mr-6 flex-shrink-0">
                    <div className="h-20 w-20 sm:h-24 sm:w-24 rounded-full overflow-hidden bg-brand-gray-200">
                      {therapist.image ? (
                        <img 
                          src={therapist.image} 
                          alt={therapist.name} 
                          className="w-full h-full object-cover"
                        />
                      ) : (
                        <div className="h-full w-full flex items-center justify-center bg-brand-blue-100">
                          <span className="text-4xl font-bold text-brand-blue-500">
                            {therapist.name.charAt(0)}
                          </span>
                        </div>
                      )}
                    </div>
                  </div>
                  
                  <div className="flex-1">
                    <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between">
                      <div>
                        <h1 className="text-xl sm:text-2xl font-bold text-brand-gray-900 mb-2">
                          {therapist.name}
                        </h1>
                        <div className="flex items-center mb-2">
                          <Star className="h-4 w-4 text-yellow-400 mr-1" />
                          <span className="font-medium">{therapist.rating}</span>
                          <span className="text-brand-gray-500 ml-1">({therapist.reviewCount} {t('reviews')})</span>
                        </div>
                      </div>
                      
                      {/* Book Now button for medium screens and up */}
                      <div className="hidden sm:block mt-2 sm:mt-0">
                        <Button 
                          onClick={scrollToBookingSection}
                          className="flex items-center"
                          size="sm"
                        >
                          <Calendar className="mr-1 h-4 w-4" />
                          Book Now
                        </Button>
                      </div>
                    </div>
                    
                    <div className="flex flex-wrap gap-2 mb-3">
                      {therapist.specialties.slice(0, 3).map((specialty, index) => (
                        <Badge 
                          key={index}
                          variant="secondary" 
                          className="bg-brand-gray-100 text-brand-gray-700 hover:bg-brand-gray-200"
                        >
                          {specialty}
                        </Badge>
                      ))}
                      {therapist.specialties.length > 3 && (
                        <Badge 
                          variant="outline" 
                          className="text-brand-gray-500 border-brand-gray-200"
                        >
                          +{therapist.specialties.length - 3}
                        </Badge>
                      )}
                    </div>
                    
                    <div className="flex flex-wrap items-center gap-x-4 gap-y-2 mt-2 text-brand-gray-600">
                      {therapist.location && (
                        <div className="flex items-center">
                          <MapPin className="h-4 w-4 mr-1 text-brand-gray-400" />
                          <span>{therapist.location}</span>
                        </div>
                      )}
                      
                      {therapist.isOnline && (
                        <div className="flex items-center">
                          <Video className="h-4 w-4 mr-1 text-green-500" />
                          <span className="text-green-600">{t('online')}</span>
                        </div>
                      )}
                    </div>
                    
                    {/* Book Now button for small screens only */}
                    <div className="sm:hidden mt-4">
                      <Button 
                        onClick={scrollToBookingSection}
                        className="w-full flex items-center justify-center"
                      >
                        <Calendar className="mr-1 h-4 w-4" />
                        Book Now
                      </Button>
                    </div>
                  </div>
                </div>
                
                {/* Highlighted summary for the therapist */}
                {bioInfo?.summary && (
                  <div className="mt-4 p-3 bg-brand-blue-50 rounded-lg border border-brand-blue-100">
                    <p className="text-brand-gray-800 italic text-sm">{bioInfo.summary}</p>
                  </div>
                )}
              </div>
              
              <Tabs 
                defaultValue="about" 
                value={activeTab}
                onValueChange={handleTabChange}
                className="w-full"
              >
                <TabsList className="grid grid-cols-3 border-b border-brand-gray-100">
                  <TabsTrigger value="about">{t('about')}</TabsTrigger>
                  <TabsTrigger value="reviews">{t('reviews')}</TabsTrigger>
                  <TabsTrigger value="contact">{t('contact')}</TabsTrigger>
                </TabsList>
                
                <TabsContent value="about" className="p-4 sm:p-6">
                  <h2 className="text-lg font-semibold text-brand-gray-900 mb-3">{t('aboutMe')}</h2>
                  <p className="text-brand-gray-700 text-sm mb-5">
                    {bioInfo?.fullBio || "Lorem ipsum dolor sit amet, consectetur adipiscing elit. Phasellus vel augue eu arcu scelerisque hendrerit id sed magna. Cras commodo malesuada orci non semper."}
                  </p>
                  
                  <div className="mb-5">
                    <div className="flex items-center mb-2">
                      <Heart className="h-4 w-4 text-brand-blue-500 mr-2" />
                      <h3 className="text-md font-semibold text-brand-gray-900">Therapeutic Approach</h3>
                    </div>
                    <p className="text-brand-gray-700 text-sm pl-6">
                      {bioInfo?.approach || "I utilize an integrative approach tailored to each client's unique needs, drawing from evidence-based practices including Cognitive Behavioral Therapy, mindfulness techniques, and solution-focused methods."}
                    </p>
                  </div>
                  
                  <div className="mb-5">
                    <div className="flex items-center mb-2">
                      <Award className="h-4 w-4 text-brand-blue-500 mr-2" />
                      <h3 className="text-md font-semibold text-brand-gray-900">Specialization</h3>
                    </div>
                    <ul className="list-disc pl-10 text-brand-gray-700 text-sm">
                      {therapist.specialties.map((specialty, index) => (
                        <li key={index} className="mb-1">{specialty}</li>
                      ))}
                    </ul>
                  </div>
                  
                  <div>
                    <div className="flex items-center mb-2">
                      <BookOpen className="h-4 w-4 text-brand-blue-500 mr-2" />
                      <h3 className="text-md font-semibold text-brand-gray-900">Education & Credentials</h3>
                    </div>
                    <ul className="list-disc pl-10 text-brand-gray-700 text-sm">
                      {bioInfo?.education ? (
                        bioInfo.education.map((item, index) => (
                          <li key={index} className="mb-1">{item}</li>
                        ))
                      ) : (
                        <>
                          <li className="mb-1">Ph.D. in Clinical Psychology, Stanford University</li>
                          <li className="mb-1">M.A. in Psychology, Columbia University</li>
                          <li>Certified in Brief Strategic Psychotherapy by Dr. José Luis Marín</li>
                        </>
                      )}
                    </ul>
                  </div>
                </TabsContent>
                
                <TabsContent value="reviews" className="p-4 sm:p-6">
                  <h2 className="text-lg font-semibold text-brand-gray-900 mb-4">
                    {t('reviews')} ({therapist.reviewCount})
                  </h2>
                  
                  <div className="space-y-4">
                    <div className="border-b border-brand-gray-100 pb-4">
                      <div className="flex justify-between items-center mb-2">
                        <div className="flex items-center">
                          <div className="bg-brand-blue-100 h-8 w-8 rounded-full flex items-center justify-center mr-3">
                            <span className="text-brand-blue-500 font-medium">J</span>
                          </div>
                          <span className="font-medium text-brand-gray-900">John D.</span>
                        </div>
                        <div className="flex items-center">
                          <Star className="h-4 w-4 text-yellow-400" />
                          <Star className="h-4 w-4 text-yellow-400" />
                          <Star className="h-4 w-4 text-yellow-400" />
                          <Star className="h-4 w-4 text-yellow-400" />
                          <Star className="h-4 w-4 text-yellow-400" />
                        </div>
                      </div>
                      <p className="text-brand-gray-700 text-sm">
                        Great therapist, very attentive and helpful with my anxiety issues.
                      </p>
                    </div>
                    
                    <div className="border-b border-brand-gray-100 pb-4">
                      <div className="flex justify-between items-center mb-2">
                        <div className="flex items-center">
                          <div className="bg-brand-green-100 h-8 w-8 rounded-full flex items-center justify-center mr-3">
                            <span className="text-brand-green-500 font-medium">S</span>
                          </div>
                          <span className="font-medium text-brand-gray-900">Sarah M.</span>
                        </div>
                        <div className="flex items-center">
                          <Star className="h-4 w-4 text-yellow-400" />
                          <Star className="h-4 w-4 text-yellow-400" />
                          <Star className="h-4 w-4 text-yellow-400" />
                          <Star className="h-4 w-4 text-yellow-400" />
                          <Star className="h-4 w-4 text-brand-gray-300" />
                        </div>
                      </div>
                      <p className="text-brand-gray-700 text-sm">
                        I've been seeing them for a few months now and have made significant progress with my issues.
                      </p>
                    </div>
                  </div>
                </TabsContent>
                
                <TabsContent value="contact" className="p-4 sm:p-6">
                  <h2 className="text-lg font-semibold text-brand-gray-900 mb-4">{t('contactInformation')}</h2>
                  
                  <div className="space-y-4">
                    <div className="flex items-start">
                      <Mail className="h-5 w-5 text-brand-gray-400 mr-3 mt-0.5" />
                      <div>
                        <p className="font-medium text-brand-gray-900">Email</p>
                        <p className="text-brand-gray-700 text-sm">contact@example.com</p>
                      </div>
                    </div>
                    
                    <div className="flex items-start">
                      <Phone className="h-5 w-5 text-brand-gray-400 mr-3 mt-0.5" />
                      <div>
                        <p className="font-medium text-brand-gray-900">{t('phone')}</p>
                        <p className="text-brand-gray-700 text-sm">+1 (555) 123-4567</p>
                      </div>
                    </div>
                    
                    {therapist.location && (
                      <div className="flex items-start">
                        <MapPin className="h-5 w-5 text-brand-gray-400 mr-3 mt-0.5" />
                        <div>
                          <p className="font-medium text-brand-gray-900">{t('officeAddress')}</p>
                          <p className="text-brand-gray-700 text-sm">123 Therapy St, Suite 45<br />New York, NY 10001</p>
                        </div>
                      </div>
                    )}
                  </div>
                </TabsContent>
              </Tabs>
            </div>
          </div>
          
          {/* Right column - Booking card (sticky on desktop) */}
          <div className="md:col-span-1" id="booking-section">
            <div className="md:sticky md:top-24">
              <TherapistBookingCard
                therapistId={id || ''}
                therapistName={therapist.name}
                sessionPrice={therapist.price}
                trialPrice={therapist.price * 0.7}
                hasInPerson={!!therapist.location}
                weeklyAvailability={weeklyAvailability}
                specificDates={specificDates}
                existingAppointments={existingAppointments}
                userBalance={userBalance}
              />
            </div>
          </div>
        </div>
        
        {/* Mobile CTA now handled by MobileStickyCTA component */}
      </div>
    </>
  );
};

export default TherapistProfile;
